// @flow
import React from 'react';
import {Helmet} from 'react-helmet-async';
import {injectIntl} from 'react-intl';
import {compose} from 'redux';
import {connect} from 'react-redux';

import {makePageURL} from '../../utils/routerUtils';
import withSeoMetaData from '../withSeoMetaData/withSeoMetaData';

import type {IntlShape} from 'react-intl';
import type {State as ReduxState} from '../../reducers/index';
import type {FacebookData} from '../../../server/utils/systemData';
import type {WithSeoMetaDataProps} from '../withSeoMetaData/withSeoMetaData';

export type AlternativeLink = {
    link: string,
    hrefLang: string
};

type StateProps = {
    brand: string,
    facebook: FacebookData,
    searchParams: *,
    nextPageUrl?: string,
    prevPageUrl?: string
};

type OwnProps = {
    canonicalLink?: string,
    alternativeLinks?: AlternativeLink[],
    page?: number,
    pageCount?: number
};

type IntlProps = {intl: IntlShape};

type Props = OwnProps & IntlProps & StateProps & WithSeoMetaDataProps;

const MESSAGE_DEFINITION = {
    fallback: {
        id: 'page.title.fallback',
        defaultMessage: 'Vacation Rentals',
        description: 'page title'
    }
};

const HtmlTitle = (props: Props) => {
    const {
        facebook,
        intl: {formatMessage},
        seoData = {},
        page = 0,
        pageCount = 0,
        nextPageUrl,
        prevPageUrl
    } = props;

    const {
        pageTitle,
        metaDescription,
        metaKeywords,
        canonicalLink: canonicalSerpLink,
        canonicalSlpLink,
        alternativeLinks
    } = seoData;
    const hasPrevUrl = nextPageUrl && page > 1;
    const hasNextUrl = prevPageUrl && page < pageCount;
    const htmlTitleText =
        pageTitle || formatMessage(MESSAGE_DEFINITION.fallback);
    const canonicalLink = canonicalSlpLink || canonicalSerpLink;

    return (
        <Helmet defaultTitle="Vacation Rentals">
            <title>{htmlTitleText}</title>
            {canonicalLink ? (
                <link rel="canonical" href={canonicalLink} />
            ) : null}
            {metaKeywords ? (
                <meta name="keywords" content={metaKeywords} />
            ) : null}
            {metaDescription ? (
                <meta name="description" content={metaDescription} />
            ) : null}
            <meta property="fb:admins" content={facebook.admins} />
            <meta property="fb:app_id" content={facebook.appId} />
            {hasPrevUrl && <link rel={'prev'} href={prevPageUrl} />}
            {hasNextUrl && <link rel={'next'} href={nextPageUrl} />}
            {alternativeLinks &&
                alternativeLinks.map(({link, hrefLang}) => (
                    <link
                        key={link}
                        hrefLang={hrefLang}
                        href={link}
                        rel={'alternate'}
                    />
                ))}
        </Helmet>
    );
};

const mapStateToProps = (
    state: ReduxState,
    {page = 0}: OwnProps
): StateProps => {
    const {
        system: {brand, facebook},
        searchParams: {params}
    } = state;

    return {
        brand,
        facebook,
        searchParams: params,
        nextPageUrl: makePageURL(page + 1, state),
        prevPageUrl: makePageURL(page - 1, state)
    };
};

export const HtmlTitleWithSeoData = compose(
    connect(mapStateToProps),
    injectIntl,
    withSeoMetaData
)(HtmlTitle);

export default HtmlTitle;
